/**
 * EPUB 解析器 — 使用 epubjs 提取文本并 block 化
 *
 * epubjs API：
 *   book.spine.each(section => ...) 遍历章节
 *   section.load(request) → Document
 *   从 Document DOM 提取 h1/h2/h3/p/blockquote/pre
 *
 * 对齐 features.md 1.2：EPUB 导入
 */

import type { Block } from '@hump/shared-types';
import type { Parser, ParseResult } from './types';
import { createBlock } from './block-builder';

/** Blob → data URL */
async function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

export class EpubParser implements Parser {
  readonly sourceType = 'epub' as const;

  async parse(input: ArrayBuffer): Promise<ParseResult> {
    const ePub = (await import('epubjs')).default;

    const book = ePub(input);
    const blocks: Block[] = [];
    let order = 0;
    let title = 'Untitled';

    // 从元数据提取标题和语言
    let lang = 'en';
    try {
      const metadata = await book.loaded.metadata;
      if (metadata?.title) title = metadata.title;
      if (metadata?.language) lang = metadata.language;
    } catch {
      // 元数据读取失败，使用默认值
    }

    // 用 each 遍历 spine 章节，收集所有 load promise
    const sectionPromises: Promise<void>[] = [];

    book.spine.each((section: SectionLike) => {
      if (!section?.href) return;

      sectionPromises.push(
        section
          .load(book.load.bind(book))
          .then((doc: Document | undefined) => {
            if (!doc) return;
            this.extractBlocks(doc, blocks, () => order, (newOrder) => {
              order = newOrder;
            }, () => {
              if (title === 'Untitled') {
                const h1 = doc.querySelector('h1');
                if (h1?.textContent?.trim()) title = h1.textContent.trim();
              }
            });
            section.unload();
          })
          .catch(() => {
            // 单 section 失败跳过
          })
      );
    });

    // 等待所有 section 加载完成（不用 setTimeout）
    await Promise.all(sectionPromises);

    book.destroy();

    return {
      blocks,
      meta: { title, sourceType: 'epub', lang },
    };
  }

  /** 从 DOM Document 提取 block 元素 */
  private extractBlocks(
    doc: Document,
    blocks: Block[],
    getOrder: () => number,
    setOrder: (n: number) => void,
    onHeading: () => void
  ): void {
    const body = doc.body || doc.documentElement;
    if (!body) return;

    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_ELEMENT, {
      acceptNode(node: Element) {
        const tag = node.tagName?.toLowerCase();
        if (
          tag === 'h1' || tag === 'h2' || tag === 'h3' ||
            tag === 'p' || tag === 'blockquote' || tag === 'pre' ||
            tag === 'img'
        ) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      },
    });

    const elements: Element[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      elements.push(node as Element);
    }

    for (const el of elements) {
      const tag = el.tagName.toLowerCase();

      // 1.13 EPUB 图片提取
      if (tag === 'img') {
        const src = (el as HTMLImageElement).getAttribute('src') || '';
        if (!src) continue;
        // 使用 src 作为 imageData（data: URL 直接可用；相对路径由渲染层处理）
        const alt = el.getAttribute('alt') || '';
        blocks.push(
          createBlock({
            docId: '',
            order: getOrder(),
            type: 'image',
            imageData: src,
            caption: alt || undefined,
            original: alt,
          })
        );
        continue;
      }

      const text = (el.textContent || '').trim();
      if (!text) continue;

      let type: Block['type'] = 'paragraph';
      let level: number | undefined;

      if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
        type = 'heading';
        level = tag === 'h1' ? 1 : tag === 'h2' ? 2 : 3;
      } else if (tag === 'blockquote') {
        type = 'quote';
      } else if (tag === 'pre') {
        type = 'code';
      }

      blocks.push(
        createBlock({
          docId: '',
          order: getOrder(),
          type,
          level,
          original: text,
        })
      );
      setOrder(getOrder() + 1);
    }

    onHeading();
  }
}

/** epubjs Section 的最小类型（避免依赖其不完整类型声明） */
interface SectionLike {
  href?: string;
  load(request?: unknown): Promise<Document | undefined>;
  unload(): void;
}
